// store/useJobPostStore.ts
import { create } from "zustand";

const baseUrl = "https://admin.talentlinkindo.com/api";

// ====================
// Tipe data Job dari API
// ====================
export type JobAdminAPI = {
  id: number | string;
  name: { en: string; id: string };
  company_name: string;
  city?: { id: string, name: string };
  state?: { id: string, name: string };
  country?: { id: string, name: string };
  degree?: { id: { en: string; id: string } };
  experience_year_id?: number;
  experience_level_id?: { id: string };
  responsibility?: { en: string; id: string };
  requirement?: { en: string; id: string };
  skills?: { id: number; name: string }[];
  benefits?: { id: number; name: { en: string; id: string } | string }[];
  min_salary?: number | null;
  max_salary?: number | null;
  show_salary?: boolean;
  expire_at?: string;
  status?: string;
  created_at?: string;
  total_applicants?: number;
  company_size_id?: string | null;
  company_profile_id?: number | null;
  industry_id?: number | null;
  working_type_id?: number | null;
  job_type_id?: number | null;
  country_id?: string | null;
  state_id?: string | null;
  city_id?: string | null;
};

// ====================
// Status
// ====================
export type StatusType = "draft" | "reviewed" | "open" | "rejected" | "closed";

// ====================
// JobDetail untuk store
// ====================
type JobDetailType = JobAdminAPI & { id: string };

// ====================
// Store
// ====================
type JobPostStore = {
  jobs: Record<StatusType, JobAdminAPI[]>;
  jobDetail: JobDetailType | null;
  loading: boolean;
  error: string | null;

  fetchJobs: (lang: "en" | "id", status: StatusType) => Promise<void>;
  fetchAllJobs: (lang: "en" | "id") => Promise<void>;
  fetchJobDetail: (lang: "en" | "id", id: string) => Promise<void>;
  submitJobPost: (payload: any) => Promise<{ success: boolean; data?: any; error?: any, message?: any, }>;
  deleteJob: (jobId: any) => Promise<{
    message: string; success: boolean; data?: any; error?: any
  }>;
  submitUpdateJobPost: (payload: any, jobId: any) => Promise<{ success: boolean; data?: any; error?: any, message?: any, }>;
};

// ====================
// Helper: token + headers
// ====================
const getAccessToken = (): string | null => {
  if (typeof window === "undefined") return null;
  try {
    const raw = localStorage.getItem("auth-storage");
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed?.state?.accessToken ?? null;
  } catch (e) {
    console.error("❌ Failed to parse auth token:", e);
    return null;
  }
};

const getHeaders = () => {
  const token = getAccessToken();
  return {
    Accept: "application/json",
    "Content-Type": "application/json",
    Authorization: token ? `Bearer ${token}` : "",
  };
};

const handleUnauthorized = () => {
  localStorage.removeItem("auth-storage");
  window.location.href = "/";
};

// ====================
// Store
// ====================
export const useJobPostStore = create<JobPostStore>((set) => ({
  jobs: {
    draft: [],
    reviewed: [],
    open: [],
    rejected: [],
    closed: [],
  },
  jobDetail: null,
  loading: false,
  error: null,

  // ====================
  // Fetch jobs by status
  // ====================
  fetchJobs: async (lang, status) => {
    set({ loading: true, error: null });
    try {
      const res = await fetch(`${baseUrl}/company/vacancies/filter/${status}`, {
        headers: getHeaders(),
      });
      if (res.status === 401) return handleUnauthorized();
      const json = await res.json();

      const list: JobAdminAPI[] = (json?.data || []).map((j: any) => ({
        ...j,
        min_salary: j.min_salary != null ? Number(j.min_salary) : null,
        max_salary: j.max_salary != null ? Number(j.max_salary) : null,
      }));

      set((state) => ({ jobs: { ...state.jobs, [status]: list }, loading: false }));
    } catch (err) {
      console.error("❌ Error fetchJobs:", err);
      set({ loading: false, error: "Failed to fetch job list" });
    }
  },

  // ====================
  // Fetch all jobs
  // ====================
  fetchAllJobs: async (lang) => {
    const statuses: StatusType[] = ["draft", "reviewed", "open", "rejected", "closed"];
    set({ loading: true, error: null });
    try {
      const results = await Promise.all(
        statuses.map((st) =>
          fetch(`${baseUrl}/company/vacancies/filter/${st}`, { headers: getHeaders() })
            .then((r) => {
              if (r.status === 401) handleUnauthorized();
              return r.json();
            })
            .catch(() => ({ data: [] }))
        )
      );

      const obj: Record<StatusType, JobAdminAPI[]> = {
        draft: [],
        reviewed: [],
        open: [],
        rejected: [],
        closed: [],
      };

      results.forEach((result, idx) => {
        const st = statuses[idx];
        obj[st] = (result?.data || []).map((j: any) => ({
          ...j,
          min_salary: j.min_salary != null ? Number(j.min_salary) : null,
          max_salary: j.max_salary != null ? Number(j.max_salary) : null,
        }));
      });

      set({ jobs: obj, loading: false });
    } catch (err) {
      console.error("❌ Error fetchAllJobs:", err);
      set({ loading: false, error: "Failed to fetch job lists" });
    }
  },

  // ====================
  // Fetch single job detail
  // ====================
  fetchJobDetail: async (lang, id) => {
    set({ loading: true, error: null });
    try {
      const res = await fetch(`${baseUrl}/company/vacancies/${id}`, { headers: getHeaders() });
      if (res.status === 401) return handleUnauthorized();
      const json = await res.json();
      const j = json?.data;
      if (!j) throw new Error("Job not found");

      const parsed: JobDetailType = {
        ...j,
        id: j.id.toString(),
        min_salary: j.min_salary != null ? Number(j.min_salary) : null,
        max_salary: j.max_salary != null ? Number(j.max_salary) : null,
      };

      set({ jobDetail: parsed, loading: false });
    } catch (err) {
      console.error("❌ Error fetchJobDetail:", err);
      set({ loading: false, error: "Failed to fetch job detail", jobDetail: null });
    }
  },

  // ====================
  // Submit job post
  // ====================
  submitJobPost: async (payload) => {
    set({ loading: true, error: null });
    try {
      const res = await fetch(`${baseUrl}/company/vacancies`, {
        method: "POST",
        headers: getHeaders(),
        body: JSON.stringify(payload),
      });

      if (res.status === 401) {
        handleUnauthorized();
        return { success: false, error: "Unauthorized" };
      }

      if (!res.ok) {
        const err = await res.json();
        return { success: false, error: err };
      }

      const data = await res.json();
      set({ loading: false });
      return { success: true, data };
    } catch (error) {
      console.error("❌ Error submitJobPost:", error);
      set({ loading: false, error: "Failed to submit job post" });
      return { success: false, error };
    }
  },


  submitUpdateJobPost: async (payload: any, jobId: string | number) => {
    set({ loading: true, error: null });
    try {
      const url = jobId
        ? `${baseUrl}/company/vacancies/${jobId}` // edit job
        : `${baseUrl}/company/vacancies`;         // create job

      const method = jobId ? "POST" : "POST"; // PUT untuk edit, POST untuk create

      const res = await fetch(url, {
        method,
        headers: getHeaders(),
        body: JSON.stringify(payload),
      });

      if (res.status === 401) {
        handleUnauthorized();
        return { success: false, error: "Unauthorized" };
      }

      if (!res.ok) {
        const err = await res.json();
        return { success: false, error: err };
      }

      const data = await res.json();
      set({ loading: false });
      return { success: true, data };
    } catch (error) {
      console.error("❌ Error submitJobPost:", error);
      set({ loading: false, error: "Failed to submit job post" });
      return { success: false, error };
    }
  },

  // Tambahkan di dalam store useJobPostStore
  deleteJob: async (jobId: string | number) => {
    set({ loading: true, error: null });
    try {
      const res = await fetch(`${baseUrl}/company/vacancies/${jobId}`, {
        method: "DELETE",
        headers: getHeaders(),
      });

      if (res.status === 401) {
        handleUnauthorized();
        return { success: false, message: "Unauthorized", error: "Unauthorized" };
      }

      const json = await res.json();

      if (!res.ok) {
        return { success: false, message: json.message || "Failed to delete job", error: json };
      }

      set({ loading: false });
      return { success: true, message: json.message || "Job deleted successfully" };
    } catch (error: any) {
      console.error("❌ Error deleteJob:", error);
      set({ loading: false, error: "Failed to delete job" });
      return { success: false, message: "Failed to delete job", error };
    }
  },



}));
